import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Surfaced visibly via render(); also log for the console panel.
    // eslint-disable-next-line no-console
    console.error("App error boundary caught:", error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-ink-950 p-6 text-slate-100">
          <div className="surface w-full max-w-xl p-6">
            <div className="text-xs uppercase tracking-wider text-red-300">
              Application error
            </div>
            <h1 className="mt-2 text-xl font-semibold text-white">
              Something went wrong while rendering the app.
            </h1>
            <p className="mt-2 break-words text-sm text-slate-300">
              {this.state.error.message || String(this.state.error)}
            </p>
            <p className="mt-2 text-xs text-slate-400">
              Check the browser console for the full stack trace.
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-5 inline-flex items-center rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-ink-950 hover:bg-accent-soft"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
